import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import AdminAssistant from "@/components/AdminAssistant";
import OnboardClientDialog from "@/components/OnboardClientDialog";
import { ActivationBadge, PipelineFilter, applyPipelineFilter, pipelineCounts } from "@/components/ActivationBadge";
import AutomationsCard from "@/components/AutomationsCard";
import InboundUnmatched from "@/components/InboundUnmatched";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ShieldCheck, FileText, Download, Loader2, Users, FolderOpen, CheckCircle2, ClipboardList, Upload, Target, Mail, Rocket,
  Briefcase, UserPlus, Trash2, ExternalLink,
} from "lucide-react";

const STATUSES = ["Intake", "Review", "Filed", "Complete"];
const STATUS_COLORS = {
  Onboarding: "bg-[#EDE9FE] text-[#5B21B6]",
  Vault: "bg-[#CFFAFE] text-[#155E75]",
  Intake: "bg-[#E6F0EB] text-[#114A31]",
  Review: "bg-[#FEF3C7] text-[#92600A]",
  Filed: "bg-[#DBEAFE] text-[#1E40AF]",
  Complete: "bg-[#DCFCE7] text-[#166534]",
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [leads, setLeads] = useState([]);
  const [techs, setTechs] = useState([]);
  const [techForm, setTechForm] = useState({ name: "", email: "", temp_password: "" });
  const [creatingTech, setCreatingTech] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [compliance, setCompliance] = useState([]);
  const [pipelineFilter, setPipelineFilter] = useState("all");
  const visibleCases = applyPipelineFilter(cases, pipelineFilter);
  const onInviteResent = (caseId, data) =>
    setCases((cs) => cs.map((c) => c.case_id === caseId
      ? { ...c, last_invite_at: new Date().toISOString(), invite_count: (c.invite_count || 1) + 1 } : c));

  const loadCompliance = () => api.get("/admin/compliance-overview").then((r) => setCompliance(r.data)).catch(() => {});

  const load = () => {
    Promise.all([api.get("/admin/cases"), api.get("/admin/stats"), api.get("/admin/leads"), api.get("/admin/techs")])
      .then(([c, s, l, t]) => {
        setCases(c.data);
        setStats(s.data);
        setLeads(l.data);
        setTechs(t.data);
      })
      .catch(() => toast.error("Could not load admin data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user && user.role === "admin") { load(); loadCompliance(); }
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const changeStatus = async (caseId, status) => {
    setUpdatingId(caseId);
    try {
      await api.post(`/admin/cases/${caseId}/status`, { status });
      setCases((prev) => prev.map((c) => (c.case_id === caseId ? { ...c, status } : c)));
      toast.success(`Case moved to ${status}`);
      api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    } catch {
      toast.error("Could not update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const downloadFile = async (storagePath, filename) => {
    try {
      const res = await api.get(`/files/${storagePath}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file");
    }
  };

  const deliverDoc = (caseId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.png,.jpg,.jpeg,.doc,.docx";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      setUpdatingId(caseId);
      try {
        await api.post(`/admin/cases/${caseId}/deliver`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Document delivered to client");
        load();
      } catch {
        toast.error("Could not deliver document");
      } finally {
        setUpdatingId(null);
      }
    };
    input.click();
  };

  const createTech = async () => {
    if (!techForm.name || !techForm.email || techForm.temp_password.length < 8) {
      toast.error("Name, email and an 8+ char temporary password are required.");
      return;
    }
    setCreatingTech(true);
    try {
      await api.post("/admin/techs", techForm);
      toast.success(`Specialist ${techForm.name} created`);
      setTechForm({ name: "", email: "", temp_password: "" });
      api.get("/admin/techs").then(({ data }) => setTechs(data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create specialist");
    } finally {
      setCreatingTech(false);
    }
  };

  const deleteTech = async (techId) => {
    try {
      await api.delete(`/admin/techs/${techId}`);
      setTechs((t) => t.filter((x) => x.user_id !== techId));
      load();
    } catch {
      toast.error("Could not remove specialist");
    }
  };

  const assignTech = async (caseId, techId) => {
    try {
      await api.post(`/admin/cases/${caseId}/assign`, { tech_id: techId || null });
      setCases((prev) => prev.map((c) => (c.case_id === caseId ? { ...c, assigned_tech_id: techId || null } : c)));
      toast.success(techId ? "Specialist assigned" : "Unassigned");
    } catch {
      toast.error("Could not assign specialist");
    }
  };
  const statCards = [
    { label: "Total cases", value: stats.total, icon: FolderOpen },
    { label: "In Review", value: stats.by_status?.Review || 0, icon: ClipboardList },
    { label: "Filed", value: stats.by_status?.Filed || 0, icon: FileText },
    { label: "Complete", value: stats.by_status?.Complete || 0, icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F8] font-body">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-12 pt-28 pb-20">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#114A31] flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5" /> Admin Console
        </span>
        <h1 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
          Full-Service Case Management
        </h1>
        <p className="mt-2 text-[#595D5A]">Advance each client through Intake → Review → Filed → Complete.</p>

        <div className="mt-5 flex flex-wrap gap-3">
          <OnboardClientDialog onCreated={load} />
          <Button
            onClick={() => navigate("/admin/growth")}
            data-testid="open-growth-hub-btn"
            className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-6 py-5"
          >
            <Rocket className="h-4 w-4 mr-2" /> Open Growth Hub
          </Button>
        </div>

        <div className="mt-8 space-y-6"><InboundUnmatched cases={cases} /><AutomationsCard /></div>

        {/* Compliance overview across all clients */}
        <section className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="compliance-overview-panel">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-[#114A31] flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-white" />
            </span>
            <h2 className="font-heading text-xl font-semibold text-[#1C1C1C]">Compliance Overview</h2>
          </div>
          <p className="mt-1 text-sm text-[#595D5A]">S-Corp compliance progress per client (lowest first). Open a case to update items.</p>
          {compliance.length === 0 ? (
            <p className="mt-4 text-sm text-[#595D5A]">No client cases yet.</p>
          ) : (
            <div className="mt-4 space-y-2" data-testid="compliance-overview-list">
              {compliance.map((c) => (
                <button key={c.case_id} onClick={() => navigate(`/staff/case/${c.case_id}`)}
                  data-testid={`compliance-row-${c.case_id}`}
                  className="w-full flex items-center gap-4 rounded-xl border border-[#E5E5E5] px-4 py-3 hover:border-[#114A31] transition-colors text-left">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1C1C1C] truncate">{c.business_name || "—"}</p>
                    <p className="text-xs text-[#595D5A] truncate">{c.user_name || c.user_email || ""} · {c.status}</p>
                  </div>
                  <div className="w-40 shrink-0">
                    <div className="h-2 rounded-full bg-[#E6F0EB] overflow-hidden">
                      <div className="h-full bg-[#114A31]" style={{ width: `${c.pct}%` }} />
                    </div>
                  </div>
                  <span className="text-sm text-[#595D5A] w-24 text-right shrink-0">{c.done}/{c.total} · {c.pct}%</span>
                </button>
              ))}
            </div>
          )}
        </section>
        <section className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="team-panel">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-[#114A31] flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-white" />
            </span>
            <h2 className="font-heading text-xl font-semibold text-[#1C1C1C]">Team — Specialists</h2>
          </div>
          <p className="mt-1 text-sm text-[#595D5A]">Create specialist (tech) accounts and assign them to client cases below.</p>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Input value={techForm.name} onChange={(e) => setTechForm({ ...techForm, name: e.target.value })}
              placeholder="Full name" data-testid="tech-name" className="rounded-xl border-[#E5E5E5]" />
            <Input value={techForm.email} onChange={(e) => setTechForm({ ...techForm, email: e.target.value })}
              placeholder="Email" type="email" data-testid="tech-email" className="rounded-xl border-[#E5E5E5]" />
            <Input value={techForm.temp_password} onChange={(e) => setTechForm({ ...techForm, temp_password: e.target.value })}
              placeholder="Temp password (8+ chars)" data-testid="tech-password" className="rounded-xl border-[#E5E5E5]" />
            <Button onClick={createTech} disabled={creatingTech} data-testid="create-tech-btn"
              className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
              {creatingTech ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-2" /> Add</>}
            </Button>
          </div>

          {techs.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="tech-list">
              {techs.map((t) => (
                <div key={t.user_id} data-testid={`tech-${t.user_id}`}
                  className="flex items-center gap-3 rounded-xl border border-[#E5E5E5] px-4 py-2.5">
                  <Briefcase className="h-4 w-4 text-[#114A31]" />
                  <span className="text-sm font-medium text-[#1C1C1C]">{t.name}</span>
                  <span className="text-xs text-[#595D5A]">{t.email}</span>
                  <span className="ml-auto text-xs text-[#595D5A]">{t.assigned_count} assigned</span>
                  <button onClick={() => deleteTech(t.user_id)} data-testid={`delete-tech-${t.user_id}`}
                    className="text-[#595D5A] hover:text-[#B91C1C]"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-[#595D5A]">Share the temp password with your specialist — they'll be prompted to set a new one on first login.</p>
        </section>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
              <s.icon className="h-5 w-5 text-[#114A31]" />
              <p className="mt-3 font-heading text-3xl font-bold text-[#1C1C1C]">{s.value}</p>
              <p className="text-sm text-[#595D5A]">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Smart assistant + Leads */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AdminAssistant />

          <div className="bg-white border border-[#E5E5E5] rounded-2xl flex flex-col h-[460px]" data-testid="leads-panel">
            <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-8 w-8 rounded-lg bg-[#114A31] flex items-center justify-center">
                  <Target className="h-4 w-4 text-white" />
                </span>
                <span className="font-heading font-semibold text-[#1C1C1C]">Website Leads</span>
              </div>
              <span className="text-sm text-[#595D5A]">{leads.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {leads.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-[#595D5A]">
                  <Target className="h-8 w-8 mb-2" />
                  <p className="text-sm">No leads yet. The website chatbot captures visitor emails here.</p>
                </div>
              ) : (
                leads.map((l) => (
                  <div key={l.id} className="rounded-xl border border-[#E5E5E5] p-3" data-testid={`lead-${l.id}`}>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#114A31]" />
                      <a href={`mailto:${l.email}`} className="text-sm font-medium text-[#114A31] hover:underline">
                        {l.email}
                      </a>
                      <span className="ml-auto text-[10px] uppercase tracking-wide bg-[#E6F0EB] text-[#114A31] px-2 py-0.5 rounded-full">
                        {l.status}
                      </span>
                    </div>
                    {l.context && <p className="mt-1.5 text-xs text-[#595D5A] line-clamp-2">“{l.context}”</p>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>


        {loading ? (
          <div className="mt-10 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#114A31]" />
          </div>
        ) : cases.length === 0 ? (
          <div className="mt-10 bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center" data-testid="admin-empty">
            <Users className="h-10 w-10 text-[#595D5A] mx-auto" />
            <p className="mt-3 font-medium text-[#1C1C1C]">No Full-Service cases yet</p>
            <p className="text-sm text-[#595D5A]">Cases appear here once clients submit their intake.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="admin-cases">
            <PipelineFilter value={pipelineFilter} onChange={setPipelineFilter} counts={pipelineCounts(cases)} />
            {visibleCases.length === 0 && (
              <p className="text-sm text-[#595D5A] bg-white border border-[#E5E5E5] rounded-2xl p-6 text-center" data-testid="pipeline-filter-empty">
                No cases match this filter.
              </p>
            )}
            {visibleCases.map((c) => (
              <div
                key={c.case_id}
                data-testid={`admin-case-${c.case_id}`}
                className="bg-white border border-[#E5E5E5] rounded-2xl p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-lg font-semibold text-[#1C1C1C]">{c.business_name}</h3>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[c.status] || ""}`}>
                        {c.status}
                      </span>
                      <ActivationBadge c={c} onResent={onInviteResent} />
                    </div>
                    <p className="text-sm text-[#595D5A] mt-1">
                      {c.user_name} · {c.user_email}
                    </p>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-sm text-[#1C1C1C]">
                      <span><span className="text-[#595D5A]">State:</span> {c.entity_state}</span>
                      <span><span className="text-[#595D5A]">EIN:</span> {c.ein || "—"}</span>
                      <span><span className="text-[#595D5A]">Owners:</span> {c.num_owners}</span>
                      <span><span className="text-[#595D5A]">Net profit:</span> {c.annual_revenue}</span>
                      <span><span className="text-[#595D5A]">Payroll:</span> {c.current_payroll}</span>
                    </div>
                    {c.notes && <p className="mt-2 text-sm text-[#595D5A] italic">“{c.notes}”</p>}

                    {c.files?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2" data-testid={`admin-files-${c.case_id}`}>
                        {c.files.map((f) => (
                          <button
                            key={f.file_id}
                            onClick={() => downloadFile(f.storage_path, f.original_filename)}
                            data-testid={`admin-download-${f.file_id}`}
                            className="inline-flex items-center gap-2 text-xs bg-[#F9F9F8] hover:bg-[#E6F0EB] border border-[#E5E5E5] rounded-lg px-3 py-2 text-[#1C1C1C] transition-colors"
                          >
                            <FileText className="h-3.5 w-3.5 text-[#114A31]" />
                            <span className="max-w-[160px] truncate">{f.original_filename}</span>
                            <Download className="h-3.5 w-3.5 text-[#595D5A]" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="lg:w-56 shrink-0 space-y-3">
                    <div>
                      <label className="text-xs text-[#595D5A]">Update status</label>
                      <Select
                        value={c.status}
                        onValueChange={(v) => changeStatus(c.case_id, v)}
                        disabled={updatingId === c.case_id}
                      >
                        <SelectTrigger
                          className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white"
                          data-testid={`status-select-${c.case_id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {!STATUSES.includes(c.status) && (
                            <SelectItem value={c.status} disabled data-testid={`status-option-${c.status}`}>{c.status}</SelectItem>
                          )}
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={() => deliverDoc(c.case_id)}
                      disabled={updatingId === c.case_id}
                      variant="outline"
                      data-testid={`deliver-btn-${c.case_id}`}
                      className="w-full rounded-xl border-[#E5E5E5] hover:border-[#114A31] hover:bg-[#F9F9F8] text-[#1C1C1C]"
                    >
                      <Upload className="h-4 w-4 mr-2" /> Deliver document
                    </Button>
                    <div>
                      <label className="text-xs text-[#595D5A]">Assigned specialist</label>
                      <Select
                        value={c.assigned_tech_id || "none"}
                        onValueChange={(v) => assignTech(c.case_id, v === "none" ? "" : v)}
                      >
                        <SelectTrigger className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white" data-testid={`assign-select-${c.case_id}`}>
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unassigned</SelectItem>
                          {techs.map((t) => (
                            <SelectItem key={t.user_id} value={t.user_id} data-testid={`assign-option-${t.user_id}`}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={() => navigate(`/staff/case/${c.case_id}`)}
                      data-testid={`workspace-btn-${c.case_id}`}
                      className="w-full rounded-xl bg-[#114A31] hover:bg-[#1A6B46] text-white"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" /> Open workspace
                    </Button>
                  </div>
                </div>

                {c.delivered_files?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#E5E5E5]" data-testid={`delivered-${c.case_id}`}>
                    <p className="text-xs font-bold tracking-wide uppercase text-[#114A31] mb-2">
                      Delivered to client
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {c.delivered_files.map((f) => (
                        <button
                          key={f.file_id}
                          onClick={() => downloadFile(f.storage_path, f.original_filename)}
                          className="inline-flex items-center gap-2 text-xs bg-[#E6F0EB] hover:bg-[#d4e8dd] border border-[#114A31]/20 rounded-lg px-3 py-2 text-[#114A31] transition-colors"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          <span className="max-w-[160px] truncate">{f.original_filename}</span>
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
